import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { supabase } from '../supabaseClient';
import LayoutSelector from '../components/LayoutSelector';
import { defaultConfig } from '../constants/defaultConfig';
import { mergeConfig, RestaurantConfigContext } from '../contexts/RestaurantConfigContext';
import { FiSave, FiEye, FiSmartphone, FiMonitor, FiRotateCcw } from 'react-icons/fi';
import LayoutEngine from '../components/LayoutEngine';
import ThemeProvider from '../contexts/ThemeProvider';

export default function CustomizationPage() {
    const navigate = useNavigate();
    const [restaurant, setRestaurant] = useState(null);
    const [config, setConfig] = useState(defaultConfig);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [previewMode, setPreviewMode] = useState('mobile');

    const [mockMenuItems] = useState([
        { id: '1', name: 'Signature Truffle Pasta', description: 'Creamy fettuccine with black truffle zest and parmesan.', price: 450, category: 'Main Course', type: 'veg', image_url: 'https://images.unsplash.com/photo-1523905330026-b8bd1f5f320e?auto=format&fit=crop&q=80&w=400', is_featured: true },
        { id: '2', name: 'Spicy Dragon Rolls', description: 'Tuna rolls with sriracha mayo and cucumber.', price: 380, category: 'Appetizers', type: 'non-veg', image_url: 'https://images.unsplash.com/photo-1553621042-f6e147245754?auto=format&fit=crop&q=80&w=400', is_featured: true },
        { id: '3', name: 'Virgin Mojito', description: 'Fresh mint, lime, and soda.', price: 180, category: 'Beverages', type: 'veg', image_url: 'https://images.unsplash.com/photo-1513558161293-cdaf765ed2fd?auto=format&fit=crop&q=80&w=400' }
    ]);

    useEffect(() => {
        async function loadConfig() {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return navigate('/login');
            const { data } = await supabase.from('restaurants').select('*').eq('owner_id', user.id).single();
            if (data) {
                setRestaurant(data);
                if (data.config) setConfig(mergeConfig(defaultConfig, data.config));
            }
            setLoading(false);
        }
        loadConfig();
    }, [navigate]);

    const handleConfigChange = (path, value) => {
        const nextConfig = structuredClone(config);
        const keys = path.split('.');
        let current = nextConfig;
        for (let i = 0; i < keys.length - 1; i++) {
            if (!current[keys[i]]) current[keys[i]] = {};
            current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
        setConfig(nextConfig);
    };

    const saveConfig = async () => {
        if (!restaurant) return;
        setSaving(true);
        try {
            await supabase.from('restaurants').update({ config }).eq('id', restaurant.id);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <MainLayout activeLink="Design & Menu" title="Loading Editor...">
                <div className="p-10 flex flex-col items-center justify-center min-h-[60vh] gap-4">
                    <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
                    <p className="text-sm font-semibold text-slate-500 animate-pulse">Waking up the design engine...</p>
                </div>
            </MainLayout>
        );
    }

    const categories = ['All', 'Appetizers', 'Main Course', 'Beverages'];

    // UI Helpers to reduce JSX bloat
    const ConfigToggle = ({ path, label, desc }) => {
        const val = path.split('.').reduce((acc, p) => acc && acc[p], config) ?? false;
        return (
            <div className="flex items-center justify-between py-3.5 border-b border-slate-100 dark:border-slate-800/50 last:border-0 group">
                <div className="pr-4">
                    <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200 transition-colors group-hover:text-slate-900 dark:group-hover:text-white">{label}</h4>
                    {desc && <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">{desc}</p>}
                </div>
                <button
                    role="switch"
                    aria-checked={val}
                    aria-label={`Toggle ${label}`}
                    onClick={() => handleConfigChange(path, !val)}
                    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900 active:scale-95 ${val ? 'bg-primary' : 'bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600'}`}
                >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-300 cubic-bezier(0.4, 0.0, 0.2, 1) ${val ? 'translate-x-[18px]' : 'translate-x-[2px]'}`} />
                </button>
            </div>
        );
    };

    const ConfigInput = ({ path, label, desc }) => {
        const val = path.split('.').reduce((acc, p) => acc && acc[p], config) ?? '';
        return (
            <div className="flex items-center justify-between py-3.5 border-b border-slate-100 dark:border-slate-800/50 last:border-0 group">
                <div className="pr-4">
                    <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200 transition-colors group-hover:text-slate-900 dark:group-hover:text-white">{label}</h4>
                    {desc && <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">{desc}</p>}
                </div>
                <input 
                    type="text" 
                    value={val} 
                    aria-label={label}
                    onChange={(e) => handleConfigChange(path, e.target.value)}
                    className="w-32 p-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-semibold text-slate-900 dark:text-white placeholder:text-slate-400 focus:border-primary focus:bg-white dark:focus:bg-slate-950 outline-none focus:ring-2 focus:ring-primary/20 transition-all shadow-sm"
                />
            </div>
        );
    };

    const SectionHeader = ({ title }) => (
        <h3 className="text-xs font-bold text-slate-400 dark:text-slate-500 mb-4 uppercase tracking-wider">{title}</h3>
    );

    return (
        <MainLayout activeLink="Design & Menu" title="Design Editor">
            <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-64px)] lg:overflow-hidden bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
                
                {/* ─── LEFT: SETTINGS PANEL ─── */}
                <div className="w-full lg:w-[420px] bg-white dark:bg-slate-900 border-b lg:border-r lg:border-b-0 border-slate-200 dark:border-slate-800 lg:overflow-y-auto flex flex-col scrollbar-hide shadow-xl z-10 transition-colors duration-300">
                    <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between sticky top-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl z-20">
                        <div>
                            <h2 className="text-xl font-black tracking-tight text-slate-900 dark:text-white">Design Console</h2>
                        </div>
                        <div className="flex items-center gap-4">
                            <button 
                                onClick={() => setConfig(defaultConfig)} 
                                className="text-[10px] text-slate-400 hover:text-red-500 font-black uppercase tracking-widest flex items-center gap-1.5 transition-colors active:scale-95"
                                aria-label="Reset configuration to defaults"
                            >
                                <FiRotateCcw className="w-3.5 h-3.5" /> Reset
                            </button>
                            <button 
                                onClick={saveConfig}
                                disabled={saving}
                                className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 transition-all shadow-lg active:scale-95 disabled:opacity-50 ${saved ? 'bg-emerald-500 text-white shadow-emerald-500/20' : 'bg-slate-900 dark:bg-primary text-white dark:text-charcoal hover:shadow-primary/20'}`}
                            >
                                {saving ? <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <FiSave className="w-3.5 h-3.5" />}
                                {saved ? 'Saved!' : 'Save'}
                            </button>
                        </div>
                    </div>

                    <div className="p-6 space-y-10 pb-12">
                        {/* Layout */}
                        <section>
                            <SectionHeader title="Core Layout" />
                            <LayoutSelector 
                                value={config.customization.menu_layout.type} 
                                onChange={(val) => handleConfigChange('customization.menu_layout.type', val)} 
                                primaryColor={config.customization.theme.primary_color}
                            />
                        </section>

                        {/* Branding */}
                        <section>
                            <SectionHeader title="Branding" />
                            <div className="grid grid-cols-2 gap-5">
                                <div>
                                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-2">Primary Color</label>
                                    <div className="flex items-center gap-3 p-2 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm transition-colors">
                                        <input 
                                            type="color" 
                                            value={config.customization.theme.primary_color} 
                                            onChange={(e) => handleConfigChange('customization.theme.primary_color', e.target.value)} 
                                            className="w-8 h-8 rounded border-0 bg-transparent p-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" 
                                            aria-label="Select primary color"
                                        />
                                        <span className="text-xs font-mono font-bold text-slate-700 dark:text-slate-300">{config.customization.theme.primary_color.toUpperCase()}</span>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-2">Typography</label>
                                    <select 
                                        value={config.customization.theme.font_style} 
                                        onChange={(e) => handleConfigChange('customization.theme.font_style', e.target.value)}
                                        className="w-full p-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-300 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all shadow-sm cursor-pointer"
                                        aria-label="Select typography style"
                                    >
                                        <option value="modern">Modern (Inter)</option>
                                        <option value="classic">Classic (Serif)</option>
                                        <option value="playful">Playful (Cursive)</option>
                                    </select>
                                </div>
                            </div>
                        </section>

                        {/* Features */}
                        <section>
                            <SectionHeader title="Features & Flow" />
                            <div className="rounded-2xl border border-slate-100 dark:border-slate-800/60 bg-white dark:bg-slate-900 px-4 shadow-sm">
                                <ConfigToggle path="customization.menu_sections.sticky_header" label="Sticky Header" desc="Header stays at top on scroll" />
                                <ConfigToggle path="customization.menu_sections.show_category_tabs" label="Category Tabs" desc="Show horizontal navigation" />
                                <ConfigToggle path="customization.menu_sections.show_search" label="Search Bar" desc="Allow customers to search dishes" />
                                <ConfigToggle path="customization.menu_sections.show_veg_toggle" label="Dietary Toggle" desc="Show Veg/Non-Veg filter" />
                                <ConfigToggle path="ordering.estimated_prep_time" label="Prep Time" desc="Display estimated wait times" />
                                <ConfigToggle path="customization.menu_flow.show_best_sellers_first" label="Best Sellers First" desc="Highlight popular items" />
                            </div>
                        </section>

                        {/* Cards */}
                        <section>
                            <SectionHeader title="Dish Cards" />
                            <div className="rounded-2xl border border-slate-100 dark:border-slate-800/60 bg-white dark:bg-slate-900 px-4 shadow-sm">
                                <ConfigToggle path="customization.item_card.show_image" label="Show Images" desc="Display food photos" />
                                <ConfigToggle path="customization.item_card.show_description" label="Show Descriptions" desc="Display text details" />
                                <ConfigToggle path="customization.item_card.show_veg_badge" label="Dietary Badges" desc="Render dot indicators" />
                                <ConfigInput path="customization.add_to_cart.default_text" label="Add Button Text" desc="Customize CTA label" />
                            </div>
                        </section>

                        {/* Experience */}
                        <section>
                            <SectionHeader title="Checkout Experience" />
                            <div className="rounded-2xl border border-slate-100 dark:border-slate-800/60 bg-white dark:bg-slate-900 px-4 shadow-sm">
                                <ConfigToggle path="customization.cart.enable_cart_genie" label="Cart Animations" desc="Smooth UI transitions" />
                                <ConfigToggle path="customization.upselling.enabled" label="Smart Upselling" desc="Recommend companion items" />
                                <ConfigToggle path="engagement.show_call_waiter" label="Waiter Request" desc="Digital call bell" />
                                <ConfigToggle path="customer_data.guest_checkout" label="Guest Checkout" desc="Order without login" />
                            </div>
                        </section>

                        {/* Plan Notice */}
                        <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/80 dark:to-slate-800/30 rounded-2xl p-5 flex items-center justify-between border border-slate-200/60 dark:border-slate-700/50 shadow-sm">
                            <div>
                                <h4 className="text-sm font-black text-slate-800 dark:text-slate-200">Premium Features</h4>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-0.5">Unlock more designs</p>
                            </div>
                            <button onClick={() => navigate('/subscription')} className="px-4 py-2 rounded-xl bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-xs font-bold shadow-sm border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors active:scale-95">Upgrade</button>
                        </div>
                    </div>
                </div>

                {/* ─── RIGHT: LIVE PREVIEW ─── */}
                <div className="flex-1 flex flex-col bg-slate-100/50 dark:bg-slate-950 relative overflow-hidden">
                    
                    {/* Segmented Control for Device Preview */}
                    <div className="absolute top-6 left-1/2 -translate-x-1/2 z-20 flex p-1 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-xl shadow-lg border border-slate-200/50 dark:border-slate-700/50">
                        <button 
                            onClick={() => setPreviewMode('mobile')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all duration-200 ${previewMode === 'mobile' ? 'bg-slate-900 dark:bg-slate-700 text-white shadow-md' : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'}`}
                            aria-pressed={previewMode === 'mobile'}
                        >
                            <FiSmartphone className="w-4 h-4" /> Mobile
                        </button>
                        <button 
                            onClick={() => setPreviewMode('desktop')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all duration-200 ${previewMode === 'desktop' ? 'bg-slate-900 dark:bg-slate-700 text-white shadow-md' : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'}`}
                            aria-pressed={previewMode === 'desktop'}
                        >
                            <FiMonitor className="w-4 h-4" /> Desktop
                        </button>
                    </div>

                    <div className="flex-1 flex items-center justify-center p-6 pt-24 overflow-y-auto lg:overflow-hidden">
                        <div className={`
                            relative bg-white dark:bg-slate-900 shadow-2xl overflow-hidden border-[8px] border-white dark:border-slate-800 transition-all duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)]
                            ${previewMode === 'mobile' ? 'w-[375px] h-[750px] rounded-[3rem] shrink-0' : 'w-full max-w-5xl h-full min-h-[600px] rounded-2xl'}
                        `}>
                            {/* iPhone Dynamic Island / Notch for Mobile View */}
                            {previewMode === 'mobile' && (
                                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[120px] h-[28px] bg-white dark:bg-slate-800 rounded-b-2xl z-50 shadow-sm flex justify-center items-end pb-1.5">
                                    <div className="w-12 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full" />
                                </div>
                            )}

                            <div className={`w-full h-full bg-white dark:bg-slate-950 overflow-y-auto overflow-x-hidden scrollbar-hide relative ${previewMode === 'mobile' ? 'pt-7' : ''}`}>
                                <RestaurantConfigContext.Provider value={{ config, loadingConfig: false, updateConfig: () => {} }}>
                                    <ThemeProvider>
                                        <div className="min-h-full pb-20">
                                            <header className={`${config.customization.menu_sections.sticky_header ? 'sticky top-0 z-40' : ''} bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-slate-100 dark:border-slate-800/80 flex items-center justify-between px-5 py-4 transition-colors`}>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center font-black text-slate-900 text-lg shadow-sm">R</div>
                                                    <span className="font-bold text-slate-900 dark:text-white text-base tracking-tight">{restaurant?.name || 'Preview'}</span>
                                                </div>
                                            </header>

                                            {config.customization.menu_sections.show_category_tabs && (
                                                <div className="flex gap-2.5 p-4 border-b border-slate-100 dark:border-slate-800/80 overflow-x-hidden bg-white dark:bg-slate-900">
                                                    {categories.map((c, i) => (
                                                        <span key={c} className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${i === 0 ? 'bg-primary text-slate-900 shadow-sm' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'}`}>{c}</span>
                                                    ))}
                                                </div>
                                            )}

                                            <main className="p-5 space-y-6">
                                                {config.customization.menu_sections.show_search && (
                                                    <div className="h-11 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700/50 flex items-center px-4 text-sm font-medium text-slate-400 dark:text-slate-500 shadow-sm">
                                                        <span className="material-symbols-outlined text-[18px] mr-2">search</span> Search delicious food...
                                                    </div>
                                                )}

                                                <LayoutEngine 
                                                    config={config} 
                                                    filtered={mockMenuItems} 
                                                    categories={categories}
                                                    bestSellers={config.customization.menu_flow.show_best_sellers_first ? mockMenuItems.filter(item => item.is_featured).slice(0, 5) : []}
                                                    sponsoredItems={[]}
                                                    cart={{}}
                                                    onUpdateQuantity={() => {}}
                                                    search=""
                                                    restaurant={restaurant}
                                                />
                                            </main>
                                        </div>
                                    </ThemeProvider>
                                </RestaurantConfigContext.Provider>
                            </div>
                        </div>
                    </div>

                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest flex items-center gap-2 bg-white/50 dark:bg-slate-900/50 backdrop-blur-md px-4 py-2 rounded-full border border-slate-200/50 dark:border-slate-700/50 shadow-sm">
                        <FiEye className="w-3.5 h-3.5" /> Interactive Preview
                    </div>
                </div>
            </div>
            
            <style jsx="true">{`
                .scrollbar-hide::-webkit-scrollbar { display: none; }
                .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>
        </MainLayout>
    );
}
